import { map } from "rxjs";
import { Injectable } from "@angular/core";
import { v4 as UUID } from 'uuid'
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { OrganisationService } from "../organisation.service";
import { ModelDialogComponent } from "./model-dialog.component";
import { billableProjectCount, createModelDetails, ModelDetails, Organisation, Project } from "../../common/organisation";
import { UserService } from "../../user/user.service";
import { USER_ROLE } from "../../common/user";
import { ProjectDialogComponent } from "../projects/project-dialog.component";
import { Router } from "@angular/router";
import { SKETCH_URL } from "../../app-urls";
import { CURRENT_STATE_PROJECT_KEY, MODEL_TYPE, ORGANISATION_MODEL_KEY } from "../../common/linqset-model";
import { AccessRequest, RequestDialogComponent, RequestDialogData } from "../../ui-components/dialogs/request-dialog.component";

@Injectable({
    providedIn: 'root'
  })
export class ModelService {

    constructor(private dialog: MatDialog, private organisationService: OrganisationService, private userService:UserService,
        private router:Router) {
    }

    // opens a model the user has access to, at a node if given; otherwise offers to ask for access
    editModel(organisation:Organisation, model:ModelDetails, projectKey:string, nodeKey?:string) {
        if (model.role >= 0) {
            if (model._key === ORGANISATION_MODEL_KEY) {
                this.router.navigateByUrl(SKETCH_URL(organisation._key, model._key, MODEL_TYPE.MODELS, projectKey))
            }else if (nodeKey)
                this.router.navigateByUrl(SKETCH_URL(organisation._key, model._key, MODEL_TYPE.PROCESS, projectKey) + '?nodeKey=' + encodeURIComponent(nodeKey))
            else
                this.router.navigateByUrl(SKETCH_URL(organisation._key, model._key, MODEL_TYPE.PROCESS, projectKey))
        }else {
            this.requestAccessToModel(model)
        }
    }

    requestAccessToModel(model:ModelDetails) {
        const data:RequestDialogData = {kind:'model', name:model.name}
        this.dialog.open(RequestDialogComponent, {width:"480px", data}).afterClosed().subscribe(async (request:AccessRequest|undefined) => {
            if (request) await this.organisationService.requestAccess(request.role, request.notes, undefined, model)
        })
    }

    requestAccessToProject(project:Project) {
        const data:RequestDialogData = {kind:'project', name:project.name}
        this.dialog.open(RequestDialogComponent, {width:"480px", data}).afterClosed().subscribe(async (request:AccessRequest|undefined) => {
            if (request) await this.organisationService.requestAccess(request.role, request.notes, project, undefined)
        })
    }

    openModelDialog(model?:ModelDetails, project?:Project, turnOffUpload?:boolean) {
        const isNew = model?false:true
        const projectKey = project?project._key:CURRENT_STATE_PROJECT_KEY
        const m = model || createModelDetails(undefined, undefined, projectKey)

        const dialogRef = this.dialog.open(ModelDialogComponent, this.getDialogConfig({model:m, project, isNew, turnOffUpload}));

        // the dialog returns an edited copy; remember the original so saving can update it
        return dialogRef.afterClosed().pipe(map(result => result && {...result, original: model}))
    }

    openProjectDialog(organisation:Organisation, project?:Project) {
        const isNew = project?false:true

        // a new project gives its creator admin access
        const data = project || {name:'', description:'', useOrganisationAccess:true, userAccess:[{access:USER_ROLE.ADMIN, userKey:this.userService.getUser()?._key, activated:true}]}

        const dialogRef = this.dialog.open(ProjectDialogComponent, this.getDialogConfig({project:data, isNew, numProjects:billableProjectCount(organisation.projects), exempt:organisation.isExemptFromPayment}));

        return dialogRef.afterClosed()
    }

    private getDialogConfig(data:any) {
        const dialogConfig = new MatDialogConfig();

        dialogConfig.disableClose = true;
        dialogConfig.autoFocus = true;

        dialogConfig.data = data
        dialogConfig.hasBackdrop = true
        dialogConfig.width = "800px"

        return dialogConfig
    }

    async saveModel(result:any, projectKey:string, isNew:boolean) {
        const {file, model, original} = result
        await this.organisationService.saveModel(model, projectKey, isNew, file)
        // pages holding the original (such as the open model) see the saved details
        if (original && original !== model) Object.assign(original, model)
        return model
    }

    // a project without a key is new
    async saveProject(project:any) {
        if (!project._key) {
            project._key = UUID()
            await this.organisationService.createProject(project)
        }else {
            await this.organisationService.updateProject(project)
        }
    }
}
